'use client';
import type { CustomMediaDevice } from '@huddle01/web-core/types';
import useHuddle01 from './useHuddle01';
import type { AppData } from 'mediasoup-client/lib/types';

export const useLocalMedia = () => {
  const { huddleClient } = useHuddle01();

  const localPeer = huddleClient.localPeer;

  const fetchStream = async (data: {
    mediaDeviceKind: 'mic' | 'cam';
  }) => {
    const fetchedStream = await localPeer.deviceHandler.fetchStream({
      mediaDeviceKind: data.mediaDeviceKind,
    });

    return fetchedStream;
  };

  /**
   * Fetch a Stream by the GroupId, this is useful when you want to get a stream that is already being produced.
   * `NOTE: SDK will not close this stream when you stop producing it, you will have to close it manually`
   * @param data - {groupId: string, mediaDeviceKind: 'mic' | 'cam'}
   * @throws {Error} - If the stream with the given groupId does not exist
   */
  const fetchStreamByGroupId = async (data: {
    groupId: string;
    mediaDeviceKind: Exclude<CustomMediaDevice, 'speaker'>;
  }) => {
    const fetchedStream = await localPeer.deviceHandler.fetchStreamByGroupId({
      groupId: data.groupId,
      mediaDeviceKind: data.mediaDeviceKind,
    });

    return fetchedStream;
  };

  const produceStream = (data: {
    stream: MediaStream;
    label: string;
    appData: AppData;
  }) => {
    const producing = localPeer.produce({
      label: data.label,
      stream: data.stream,
      appData: data.appData,
    });

    return producing;
  };

  const stopProducing = (label: 'mic' | 'cam' | 'speaker' | string) => {
    const stoppedProducing = localPeer.stopProducing({ label });
    return stoppedProducing;
  };

  /**
   * Replace the stream with a new stream
   * @param label - The label of the stream to replace
   * @description - If the stream with the given label is being produced, it will be stopped and replaced with the new stream.
   * @param newStream - The new stream to replace the old stream with
   * @throws {Error} - If the stream with the given label does not exist
   */
  const replaceStream = async (
    label: 'mic' | 'cam' | 'speaker' | string,
    newStream: MediaStream,
  ) => {
    return await localPeer.replaceStream({ label, newStream });
  };

  return {
    fetchStream,
    fetchStreamByGroupId,
    replaceStream,
    produceStream,
    stopProducing,
  };
};
